// Shared bottom-edge footer sampling rule (Task: match footer color to email
// in editor preview). Polished/captured (stacked-image) emails append a
// live-text footer whose background is sampled from the bottom edge of the
// LAST stacked block image, with a contrast-safe ink derived from luminance.
// Both the server send path (server/services/email-mjml.ts, sharp) and the
// editor preview (client-side canvas sampling) MUST derive their rule from
// these helpers so the two surfaces can't silently drift:
//   - which image counts as "last" → lastFooterSampleUrl (after dropping
//     baked footer blocks upstream)
//   - the strip height → footerSampleStripHeight (bottom ~2%, min 2px)
//   - the ink flip → footerInkForBg (luminance > 0.5 → dark ink, else white)
// Fallback chain (identical on both surfaces): sampled color → brand style
// page background → neutral strip.

/** Fraction of the image height sampled at the bottom edge. */
export const FOOTER_SAMPLE_STRIP_FRACTION = 0.02;
/** Minimum sampled strip height in pixels. */
export const FOOTER_SAMPLE_MIN_STRIP_PX = 2;
/** Luminance above this uses the dark ink; at/below uses the light ink. */
export const FOOTER_INK_LUMINANCE_THRESHOLD = 0.5;
export const FOOTER_DARK_INK = "#1A1A1A";
export const FOOTER_LIGHT_INK = "#FFFFFF";

/** Height (px) of the bottom-edge strip to average for an image of height h. */
export function footerSampleStripHeight(h: number): number {
  return Math.max(FOOTER_SAMPLE_MIN_STRIP_PX, Math.round(h * FOOTER_SAMPLE_STRIP_FRACTION));
}

/** Relative luminance of a #rrggbb hex (0 = black, 1 = white). */
export function hexLuminance(hex: string): number {
  const v = (hex || "").replace("#", "");
  if (v.length !== 6) return 1;
  const ch = (s: string) => {
    const c = parseInt(s, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(v.slice(0, 2)) + 0.7152 * ch(v.slice(2, 4)) + 0.0722 * ch(v.slice(4, 6));
}

/** Contrast-safe footer ink for a sampled background color. */
export function footerInkForBg(bg: string): string {
  return hexLuminance(bg) > FOOTER_INK_LUMINANCE_THRESHOLD ? FOOTER_DARK_INK : FOOTER_LIGHT_INK;
}

/**
 * The URL of the image whose bottom edge the footer samples: the LAST entry
 * with a non-empty url. Callers pass the stack AFTER dropping baked footer
 * blocks (footer/preset_trust) — same filter on send and preview.
 */
export function lastFooterSampleUrl(
  blocks: Array<{ url?: string | null } | null | undefined>,
): string | null {
  for (let i = blocks.length - 1; i >= 0; i--) {
    const u = blocks[i]?.url;
    if (typeof u === "string" && u) return u;
  }
  return null;
}
